import {
  isReference,
  NormalizedCacheObject,
  StoreObject,
  StoreValue
} from '@apollo/client';
import { DocumentNode, Kind } from 'graphql';
import { genericItemFieldsFragment } from '../../api/fragments';
import { agencyItemsFragment } from '../../api/fragments/agencyItems';
import { bookmarksFragment } from '../../api/fragments/bookmarks';

// Reference entities with no viewer-scoped fields. Only these are
// field-merged; every other entity is replaced by the incoming version.
const FIELD_MERGEABLE_TYPENAMES = new Set([
  'Location',
  'City',
  'Company',
  'Residence',
  'Agency'
]);

const getFragmentFieldNames = (document: DocumentNode) =>
  document.definitions.flatMap(definition =>
    definition.kind === Kind.FRAGMENT_DEFINITION
      ? definition.selectionSet.selections.flatMap(selection =>
          selection.kind === Kind.FIELD
            ? [(selection.alias ?? selection.name).value]
            : []
        )
      : []
  );

// Public card fields, taken from the card fragments. They are carried over
// onto a replaced Item; viewer-scoped fields (isMine, paidServices, ...) are
// not part of these fragments and are always dropped.
const ITEM_CARD_FIELDS = new Set(
  [genericItemFieldsFragment, agencyItemsFragment, bookmarksFragment].flatMap(
    getFragmentFieldNames
  )
);

const isStoreObject = (value: StoreValue): value is StoreObject =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const mergeStoreObjects = (
  existing: StoreObject,
  incoming: StoreObject
): StoreObject => {
  const result: StoreObject = { ...existing };

  Object.keys(incoming).forEach(key => {
    const existingValue = existing[key];
    const incomingValue = incoming[key];

    result[key] =
      isStoreObject(existingValue) && isStoreObject(incomingValue)
        ? mergeStoreObjects(existingValue, incomingValue)
        : incomingValue;
  });

  return result;
};

const keepItemCardFields = (existing: StoreObject, incoming: StoreObject) => {
  const result: StoreObject = { ...incoming };

  ITEM_CARD_FIELDS.forEach(field => {
    if (!(field in existing)) return;

    const isMissing = !(field in incoming);
    const isDowngradedReference =
      isReference(existing[field]) &&
      isStoreObject(incoming[field]) &&
      !isReference(incoming[field]);

    if (isMissing || isDowngradedReference) {
      result[field] = existing[field];
    }
  });

  return result;
};

export const mergeApolloCache = (
  existing: NormalizedCacheObject,
  incoming: NormalizedCacheObject
) => {
  if (!existing) return incoming;

  const result: NormalizedCacheObject = { ...existing };

  Object.keys(incoming).forEach(id => {
    const existingEntity = existing[id];
    const incomingEntity = incoming[id];

    if (!existingEntity || !incomingEntity) {
      result[id] = incomingEntity;
      return;
    }

    if (id === 'ROOT_QUERY') {
      result[id] = { ...existingEntity, ...incomingEntity };
      return;
    }

    const typename = incomingEntity.__typename ?? existingEntity.__typename;

    if (typename && FIELD_MERGEABLE_TYPENAMES.has(typename)) {
      result[id] = mergeStoreObjects(existingEntity, incomingEntity);
    } else if (typename === 'Item') {
      result[id] = keepItemCardFields(existingEntity, incomingEntity);
    } else {
      result[id] = incomingEntity;
    }
  });

  return result;
};
